import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useBookmarks } from "@/hooks/use-bookmarks";
import { usePresence } from "@/hooks/use-presence";

/** Notifies the user in-app when a saved provider comes back online. */
export function BookmarkPresenceNotifier() {
  const { bookmarks } = useBookmarks();
  const { getProviderStatus, presenceState } = usePresence();
  const prevStatus = useRef<Record<string, string>>({});
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const ids = bookmarks.map((b) => b.provider_id);
    if (ids.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("public_profiles")
        .select("user_id, full_name")
        .in("user_id", ids);
      if (cancelled || !data) return;
      const map: Record<string, string> = {};
      for (const p of data) if (p.user_id) map[p.user_id] = p.full_name || "A saved provider";
      setNames(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [bookmarks]);

  useEffect(() => {
    for (const b of bookmarks) {
      const status = getProviderStatus(b.provider_id)?.status || "offline";
      const prev = prevStatus.current[b.provider_id];
      if (prev && prev !== "online" && status === "online") {
        const name = names[b.provider_id] || "A saved provider";
        toast.success(`${name} is now online`, {
          description: "Tap to view their profile and start a chat.",
          action: {
            label: "View",
            onClick: () => {
              window.location.href = `/provider/${b.provider_id}`;
            },
          },
        });
      }
      prevStatus.current[b.provider_id] = status;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceState, bookmarks, names]);

  return null;
}

export default BookmarkPresenceNotifier;
